// Required imports
import React from "react";
import styled from "styled-components";
import { ResponsivePie } from "@nivo/pie";
import { LegendProps } from "@nivo/legends";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  ElementContainer,
  ColumnContainer,
} from "../../components/containers/styledComponents";
import { LegendCircle } from "../../components/visuals/styledComponents";
import { GHGEmissionsByPortfolio } from "../../models/controlPanelDataModel";
import { createCenteredValueLayer } from "./CenteredText";
import { generatePaletteColors } from "../../utils/graphUtils";
import { darkGreen } from "../../styles";

const StyledChartContainer = styled.div`
  width: 60%;
  height: 300px;
  border-radius: 8px;
  overflow: visible;
  margin-bottom: 20px;
`;

const LegendContainer = styled.div<{ paddingTop: number }>`
  display: flex;
  height: 275px;
  flex-direction: column;
  padding-top: ${({ paddingTop }): number => paddingTop}px;
  overflow-y: auto;
  border-radius: 8px;
`;

// The main component that renders the ResponsivePie chart
export const PortfolioEmissionsPie: React.FC<PieChartProps> = ({ data }) => {
  data = data || [];
  data.sort((a, b) => b.portfolio_contribution - a.portfolio_contribution);
  const colors = generatePaletteColors(data);

  // Transforming data into a format that can be used by Nivo Pie
  const chartData = data.map((item, index) => ({
    id: item.portfolio_index_id,
    label: item.portfolio_index_id,
    value: parseFloat((item.portfolio_contribution * 100).toFixed(2)),
    color: colors[index % colors.length],
  }));

  const totalEmissions: string =
    data.length > 0 ? data[0].portfolio_weighted_value_sum.toFixed(2) : "0";

  // eslint-disable-next-line
  const customLayers: any = [
    createCenteredValueLayer(totalEmissions, true),
    createCenteredValueLayer("tCo2e/mGBP", false),
  ];

  const layersConfig = layers.concat(customLayers);

  return (
    <>
      <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
        Emissions
      </CompassParagraph>
      <ElementContainer ai="flex-start">
        <StyledChartContainer>
          <ResponsivePie
            theme={theme}
            data={chartData}
            margin={{ top: 10, right: 15, bottom: 15, left: 15 }}
            innerRadius={0.65}
            layers={layersConfig as any} //eslint-disable-line
            activeOuterRadiusOffset={8}
            borderWidth={1}
            colors={colors}
            borderColor={{ from: "color", modifiers: [["darker", 0]] }}
            arcLabelsSkipAngle={360}
            arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
            enableArcLinkLabels={false}
            defs={definitions}
            fill={fills}
            legends={legends}
          />
        </StyledChartContainer>
        <LegendContainer paddingTop={1}>
          <ColumnContainer
            mt={1}
            jc={data.length > 8 ? "flex-start" : "center"}
            ai="flex-start"
            ml={0}
          >
            {data.map((item, index) => (
              <ElementContainer key={index} mb={5}>
                <LegendCircle color={colors[index]}></LegendCircle>
                <CompassParagraph color="#8B909A" fontSize={12}>
                  {item.portfolio_index_id}:{" "}
                  {(item.portfolio_contribution * 100).toFixed(2)}%
                </CompassParagraph>
              </ElementContainer>
            ))}
          </ColumnContainer>
        </LegendContainer>
      </ElementContainer>
    </>
  );
};

// Configurations for the Nivo Pie chart
const theme = {
  legends: { text: { fontFamily: "Apercu", color: "greyText" } },
};

const layers = ["arcs", "arcLinkLabels", "legends"];

const definitions = [
  {
    id: "dots",
    type: "patternDots",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    size: 4,
    padding: 1,
    stagger: true,
  },
  {
    id: "lines",
    type: "patternLines",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    rotation: -45,
    lineWidth: 6,
    spacing: 10,
  },
];

const fills = [
  { match: { id: "dots" }, id: "dots" },
  { match: { id: "lines" }, id: "lines" },
];

const legends: LegendProps[] = [];

// Props for the Pie chart
type PieChartProps = {
  data?: GHGEmissionsByPortfolio[];
};
